import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Upload, FileText, CheckCircle2, AlertCircle, Loader2, Trash2, ExternalLink,
  FileSearch, AlertTriangle, Mail, Send, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUREAUS = ['Experian', 'Equifax', 'TransUnion'];

const DISPUTE_REASONS = [
  { value: 'not_mine', label: 'Not my account' },
  { value: 'incorrect_balance', label: 'Incorrect balance' },
  { value: 'already_paid', label: 'Already paid in full' },
  { value: 'past_7_years', label: 'Past 7-year window' },
  { value: 'duplicate_account', label: 'Duplicate account' },
  { value: 'identity_theft', label: 'Identity theft' },
  { value: 'incorrect_date', label: 'Wrong dates' },
  { value: 'never_late', label: 'Never late — paid on time' },
  { value: 'wrong_account_type', label: 'Wrong account type' },
];

const STATUS_META = {
  uploaded: { label: 'Uploaded', color: 'bg-slate-100 text-slate-700', icon: FileText },
  parsing: { label: 'Analyzing...', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  parsed: { label: 'Ready', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-rose-100 text-rose-700', icon: AlertCircle },
};

export default function CreditReportsPage() {
  const { token } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedBureau, setSelectedBureau] = useState('Experian');
  const [parseTarget, setParseTarget] = useState(null);
  const [parsedDetail, setParsedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const fileInputRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchReports = async () => {
    try {
      const { data } = await axios.get(`${API}/credit-reports`, { headers: authHeaders });
      setReports(data.reports || []);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // Handle return from Stripe certified checkout
    handleCertifiedReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCertifiedReturn = async () => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('certified_session');
    const cancelled = params.get('certified_cancelled');
    if (cancelled) {
      toast.info('Certified upgrade cancelled — your draft is still saved');
      params.delete('certified_cancelled');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      localStorage.removeItem('pending_certified_dispute');
      return;
    }
    if (!sid) return;

    // Clean the query string so refresh doesn't re-trigger
    params.delete('certified_session');
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);

    const disputeId = localStorage.getItem('pending_certified_dispute');
    if (!disputeId) return;

    // Poll payment status (test mode usually paid within seconds)
    const toastId = toast.loading('Confirming your certified payment...');
    let paid = false;
    for (let attempt = 0; attempt < 10 && !paid; attempt++) {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`, { headers: authHeaders });
        if (data.payment_status === 'paid') {
          paid = true;
          break;
        }
      } catch (e) {
        // continue polling
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    if (!paid) {
      toast.error('Payment not confirmed yet — try approving again in a moment', { id: toastId });
      return;
    }

    // Now auto-approve the dispute
    try {
      await axios.post(
        `${API}/disputes/${disputeId}/approve`,
        { mail_class: 'certified' },
        { headers: authHeaders },
      );
      toast.success('Payment received — letter queued via certified mail', { id: toastId });
      localStorage.removeItem('pending_certified_dispute');
      await fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-approval failed after payment', { id: toastId });
    }
  };

  const handleFileSelect = () => fileInputRef.current?.click();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large (max 25 MB)');
      return;
    }

    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    form.append('bureau', selectedBureau);

    try {
      const { data } = await axios.post(`${API}/credit-reports/upload`, form, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${selectedBureau} report uploaded — analyzing now`);
      await fetchReports();
      // Auto-trigger parse
      triggerParse(data.report_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerParse = async (reportId) => {
    setParseTarget(reportId);
    // optimistic status update
    setReports((prev) => prev.map((r) => (r.report_id === reportId ? { ...r, status: 'parsing' } : r)));
    try {
      await axios.post(`${API}/credit-reports/${reportId}/parse`, {}, { headers: authHeaders });
      toast.success('Report analyzed — click "View Findings" to review');
      await fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
      await fetchReports();
    } finally {
      setParseTarget(null);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this credit report? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/credit-reports/${reportId}`, { headers: authHeaders });
      toast.success('Report deleted');
      await fetchReports();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const openDetail = async (reportId) => {
    setDetailLoading(true);
    setParsedDetail({ report_id: reportId });
    try {
      const { data } = await axios.get(`${API}/credit-reports/${reportId}`, { headers: authHeaders });
      setParsedDetail(data);
    } catch (err) {
      toast.error('Failed to load report details');
      setParsedDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setParsedDetail(null);

  const handleDialogOpenChange = async (open) => {
    if (open) return;
    // Dialog is closing — if we have an outstanding draft (user closed via X/Escape
    // without approving), silently discard it so it doesn't become an orphan.
    const orphanId = window.__pendingDraftId;
    if (orphanId) {
      try {
        await axios.post(`${API}/disputes/${orphanId}/discard`, {}, { headers: authHeaders });
      } catch (e) {
        // best-effort — either already-approved or already-discarded, either is fine
      }
      window.__pendingDraftId = null;
    }
    closeDetail();
  };

  const formatBytes = (b) => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  const uploadedBureaus = new Set(reports.filter((r) => r.status === 'parsed').map((r) => r.bureau).filter(Boolean));

  return (
    <div className="space-y-6" data-testid="credit-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Credit Reports
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Upload your free reports from annualcreditreport.com and let our AI find every disputable item.
          </p>
        </div>
      </div>

      {/* Free Report CTA */}
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-white">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-xl">
            <FileSearch className="h-6 w-6 text-teal-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Get your free credit reports</h3>
            <p className="text-sm text-slate-600 mt-0.5">
              Federal law entitles you to a free report from each bureau every week — no cost, no credit card required.
            </p>
          </div>
          <Button
            asChild
            variant="outline"
            className="border-teal-300 text-teal-700 hover:bg-teal-50"
            data-testid="open-annualcreditreport-btn"
          >
            <a href="https://www.annualcreditreport.com/" target="_blank" rel="noreferrer" className="flex items-center gap-2">
              annualcreditreport.com <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </CardContent>
      </Card>

      {/* Bureau progress */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {BUREAUS.map((bureau) => {
          const done = uploadedBureaus.has(bureau);
          return (
            <div
              key={bureau}
              className={`p-4 rounded-xl border-2 text-center transition-colors ${
                done ? 'border-emerald-300 bg-emerald-50' : 'border-dashed border-slate-200 bg-white'
              }`}
              data-testid={`bureau-status-${bureau.toLowerCase()}`}
            >
              <div className="flex items-center justify-center gap-2">
                {done ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                ) : (
                  <FileText className="h-5 w-5 text-slate-400" />
                )}
                <span className={`font-semibold ${done ? 'text-emerald-700' : 'text-slate-500'}`}>{bureau}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {done ? 'Report analyzed' : 'Not uploaded'}
              </p>
            </div>
          );
        })}
      </div>

      {/* Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upload a Report</CardTitle>
          <CardDescription>
            One report at a time. Choose the bureau this PDF is from, then select the file.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="w-full sm:w-56">
              <Select value={selectedBureau} onValueChange={setSelectedBureau}>
                <SelectTrigger data-testid="bureau-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BUREAUS.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleFileSelect}
              disabled={uploading}
              className="btn-primary flex-1"
              data-testid="upload-report-btn"
            >
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Select PDF (max 25 MB)</>
              )}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleUpload}
              className="hidden"
              data-testid="file-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Reports list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Your Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-slate-500 text-sm py-4">Loading...</div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8 text-slate-500" data-testid="reports-empty-state">
              <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm">No reports uploaded yet</p>
              <p className="text-xs mt-1">Upload your first credit report to get AI-powered dispute recommendations.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {reports.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.uploaded;
                const Icon = meta.icon;
                const isParsing = r.status === 'parsing' || parseTarget === r.report_id;
                return (
                  <div
                    key={r.report_id}
                    className="py-3 flex flex-col sm:flex-row sm:items-center gap-3"
                    data-testid={`report-row-${r.report_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="font-medium text-slate-900 truncate">
                          {r.bureau || 'Report'} · {r.original_filename}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>{formatBytes(r.size)}</span>
                        <span>·</span>
                        <span>{new Date(r.uploaded_at).toLocaleDateString()}</span>
                        {r.detected_bureau && r.bureau && r.detected_bureau !== r.bureau && (
                          <span className="text-amber-600">· AI detected {r.detected_bureau}</span>
                        )}
                        {r.parse_error && (
                          <span className="text-rose-600 truncate max-w-xs">· {r.parse_error}</span>
                        )}
                      </div>
                    </div>
                    <Badge className={`${meta.color} flex items-center gap-1`}>
                      <Icon className={`h-3 w-3 ${isParsing ? 'animate-spin' : ''}`} />
                      {isParsing ? 'Analyzing...' : meta.label}
                    </Badge>
                    <div className="flex items-center gap-2">
                      {r.status === 'uploaded' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => triggerParse(r.report_id)}
                          data-testid={`parse-btn-${r.report_id}`}
                        >
                          Analyze
                        </Button>
                      )}
                      {r.status === 'failed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => triggerParse(r.report_id)}
                          data-testid={`retry-btn-${r.report_id}`}
                        >
                          Retry
                        </Button>
                      )}
                      {r.status === 'parsed' && (
                        <Button
                          size="sm"
                          className="btn-primary"
                          onClick={() => openDetail(r.report_id)}
                          data-testid={`view-findings-btn-${r.report_id}`}
                        >
                          View Findings
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(r.report_id)}
                        data-testid={`delete-btn-${r.report_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-slate-400" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Findings + Dispute dialog */}
      <Dialog open={!!parsedDetail} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="findings-dialog">
          <FindingsDialog
            data={parsedDetail}
            loading={detailLoading}
            onClose={closeDetail}
            onDisputeQueued={fetchReports}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  FINDINGS + DISPUTE DIALOG                                                 */
/* -------------------------------------------------------------------------- */

function FindingsDialog({ data, loading, onClose, onDisputeQueued }) {
  const { token } = useAuth();
  const [stage, setStage] = useState('findings'); // findings | preview
  const [selected, setSelected] = useState({}); // item_id -> { checked, reason }
  const [generating, setGenerating] = useState(false);
  const [draftDispute, setDraftDispute] = useState(null);
  const [editedLetter, setEditedLetter] = useState('');
  const [mailClass, setMailClass] = useState('first_class');
  const [approving, setApproving] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const parsed = data?.parsed_data;
  const reportId = data?.report_id;
  const bureau = data?.bureau || parsed?.bureau;
  const negatives = parsed?.negative_items || [];
  const accounts = parsed?.accounts || [];
  const inquiries = parsed?.inquiries || [];

  useEffect(() => {
    // Reset when opening on a new report
    setStage('findings');
    setSelected({});
    setDraftDispute(null);
    setMailClass('first_class');
  }, [reportId]);

  const selectedCount = Object.values(selected).filter((s) => s?.checked).length;

  const toggle = (item) => {
    setSelected((prev) => {
      const cur = prev[item.item_id];
      return {
        ...prev,
        [item.item_id]: cur?.checked
          ? { ...cur, checked: false }
          : { checked: true, reason: cur?.reason || item.suggested_dispute_reason || 'not_mine' },
      };
    });
  };

  const changeReason = (itemId, reason) => {
    setSelected((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { checked: true }), reason },
    }));
  };

  const generateLetter = async () => {
    const chosen = negatives
      .filter((n) => selected[n.item_id]?.checked)
      .map((n) => ({
        item_id: n.item_id,
        dispute_reason: selected[n.item_id]?.reason || n.suggested_dispute_reason,
      }));
    if (chosen.length === 0) {
      toast.error('Select at least one item to dispute');
      return;
    }
    setGenerating(true);
    try {
      const { data: draft } = await axios.post(
        `${API}/credit-reports/${reportId}/generate-dispute`,
        { selected_items: chosen, mail_class: mailClass },
        { headers: authHeaders },
      );
      setDraftDispute(draft);
      setEditedLetter(draft.letter_content || '');
      window.__pendingDraftId = draft.dispute_id;
      setStage('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Letter generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const approve = async () => {
    if (!draftDispute) return;
    setApproving(true);
    try {
      // Save edits first if changed
      if (editedLetter !== draftDispute.letter_content) {
        await axios.patch(
          `${API}/disputes/${draftDispute.dispute_id}/letter`,
          { letter_content: editedLetter },
          { headers: authHeaders },
        );
      }

      // Certified path: gate behind $5.50 Stripe checkout unless already paid
      if (mailClass === 'certified' && !draftDispute.certified_paid_at) {
        // Preserve dispute so user returns to this same draft after payment
        localStorage.setItem('pending_certified_dispute', draftDispute.dispute_id);
        let co;
        try {
          const resp = await axios.post(
            `${API}/disputes/${draftDispute.dispute_id}/certified-checkout`,
            { origin_url: window.location.origin },
            { headers: authHeaders },
          );
          co = resp.data;
        } catch (checkoutErr) {
          localStorage.removeItem('pending_certified_dispute');
          throw checkoutErr;
        }
        if (co.already_paid) {
          // Fell through — retry approve
          await axios.post(
            `${API}/disputes/${draftDispute.dispute_id}/approve`,
            { mail_class: 'certified' },
            { headers: authHeaders },
          );
          localStorage.removeItem('pending_certified_dispute');
          window.__pendingDraftId = null;
          toast.success('Letter approved and queued for mailing');
          onDisputeQueued?.();
          onClose();
          return;
        }
        // Redirect to Stripe
        window.location.href = co.url;
        return;
      }

      // First-class or already-paid certified
      await axios.post(
        `${API}/disputes/${draftDispute.dispute_id}/approve`,
        { mail_class: mailClass },
        { headers: authHeaders },
      );
      window.__pendingDraftId = null;
      toast.success('Letter approved and queued for mailing');
      onDisputeQueued?.();
      onClose();
    } catch (err) {
      // Special case: 402 payment_required
      if (err.response?.status === 402) {
        toast.error('Certified mail requires the $5.50 upgrade — starting checkout');
      } else {
        toast.error(err.response?.data?.detail || 'Approval failed');
      }
    } finally {
      setApproving(false);
    }
  };

  const discard = async () => {
    if (!draftDispute) {
      setStage('findings');
      return;
    }
    if (!window.confirm('Discard this draft letter?')) return;
    try {
      await axios.post(
        `${API}/disputes/${draftDispute.dispute_id}/discard`,
        {},
        { headers: authHeaders },
      );
    } catch (err) {
      // silent — user may just want to go back
    }
    window.__pendingDraftId = null;
    setDraftDispute(null);
    setEditedLetter('');
    setStage('findings');
  };

  if (loading) {
    return (
      <div className="p-6">
        <DialogHeader>
          <DialogTitle>Loading findings...</DialogTitle>
        </DialogHeader>
        <div className="py-10 text-center text-slate-500">
          <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />
          Loading...
        </div>
      </div>
    );
  }

  if (!parsed) {
    return (
      <div className="p-6">
        <DialogHeader>
          <DialogTitle>No data</DialogTitle>
        </DialogHeader>
        <div className="py-6 text-slate-500 text-sm">This report has not been analyzed yet.</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </div>
    );
  }

  if (stage === 'preview') {
    return (
      <div className="p-6" data-testid="letter-preview-stage">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-teal-600" />
            Preview Dispute Letter — {bureau}
          </DialogTitle>
          <DialogDescription>
            Review, edit if needed, then approve to queue for mailing.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Mailing class */}
          <div className="border rounded-lg p-3 bg-slate-50">
            <div className="text-sm font-medium text-slate-900 mb-2">Mailing Method</div>
            <RadioGroup value={mailClass} onValueChange={setMailClass} className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer" htmlFor="mc-first">
                <RadioGroupItem value="first_class" id="mc-first" data-testid="mail-class-first-class" />
                <div className="text-sm">
                  <div className="font-medium text-slate-900">First-Class Mail <span className="text-emerald-700 text-xs font-semibold">(Included)</span></div>
                  <div className="text-xs text-slate-500">USPS delivery, no tracking. Standard for dispute letters.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer" htmlFor="mc-cert">
                <RadioGroupItem value="certified" id="mc-cert" data-testid="mail-class-certified" />
                <div className="text-sm">
                  <div className="font-medium text-slate-900">Certified Mail with Return Receipt <span className="text-amber-700 text-xs font-semibold">(+$5.50)</span></div>
                  <div className="text-xs text-slate-500">Legal proof of delivery — recommended for stubborn bureaus.</div>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Letter editor */}
          <div>
            <Label htmlFor="letter-content" className="text-sm font-medium">Letter Content</Label>
            <Textarea
              id="letter-content"
              value={editedLetter}
              onChange={(e) => setEditedLetter(e.target.value)}
              rows={18}
              className="font-mono text-xs mt-1"
              data-testid="letter-content-textarea"
            />
            <p className="text-xs text-slate-500 mt-1">You can edit anything above before approving. Changes save when you approve.</p>
          </div>
        </div>

        <DialogFooter className="mt-5 flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={discard} disabled={approving} data-testid="back-to-findings-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back / Discard
          </Button>
          <Button onClick={approve} disabled={approving} className="btn-primary flex-1" data-testid="approve-letter-btn">
            {approving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Queueing...</> : <><Send className="h-4 w-4 mr-2" /> Approve & Queue for Mailing</>}
          </Button>
        </DialogFooter>
      </div>
    );
  }

  // findings stage
  return (
    <div className="p-6" data-testid="findings-stage">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Manrope' }}>
          {bureau || 'Report'} Findings
        </DialogTitle>
        <DialogDescription>
          Check each disputable item you want to include, override the AI reason if needed, then generate your letter.
        </DialogDescription>
      </DialogHeader>

      <div className="mt-4 space-y-6">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryTile label="Negative Items" value={negatives.length} tone="rose" />
          <SummaryTile label="Accounts" value={accounts.length} tone="teal" />
          <SummaryTile label="Inquiries" value={inquiries.length} tone="amber" />
          <SummaryTile
            label="Credit Score"
            value={parsed.credit_score?.score || '—'}
            tone="slate"
          />
        </div>

        {/* Negative items with selection */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rose-600" /> Disputable Items ({negatives.length})
          </h3>
          {negatives.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No disputable items found. Great news!</p>
          ) : (
            <div className="space-y-2">
              {negatives.map((item) => {
                const cur = selected[item.item_id];
                const isChecked = !!cur?.checked;
                return (
                  <div
                    key={item.item_id}
                    className={`p-3 rounded-lg border transition-colors ${
                      isChecked ? 'border-teal-300 bg-teal-50/30' : 'border-rose-100 bg-rose-50/30'
                    }`}
                    data-testid={`negative-item-${item.item_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={`chk-${item.item_id}`}
                        checked={isChecked}
                        onCheckedChange={() => toggle(item)}
                        className="mt-1"
                        data-testid={`negative-item-checkbox-${item.item_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900">
                          {item.creditor_name || 'Unknown creditor'}
                          {item.amount != null && (
                            <span className="text-slate-500 font-normal"> · ${Number(item.amount).toLocaleString()}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{item.description || item.type}</div>
                        {item.dispute_reasoning && (
                          <div className="text-xs text-teal-700 mt-1 italic">AI: {item.dispute_reasoning}</div>
                        )}
                        {isChecked && (
                          <div className="mt-2">
                            <Label className="text-xs text-slate-600">Dispute reason</Label>
                            <Select
                              value={cur?.reason || item.suggested_dispute_reason || 'not_mine'}
                              onValueChange={(v) => changeReason(item.item_id, v)}
                            >
                              <SelectTrigger
                                className="mt-1 h-9 max-w-xs"
                                data-testid={`reason-select-${item.item_id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {DISPUTE_REASONS.map((r) => (
                                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Accounts summary */}
        {accounts.length > 0 && (
          <div>
            <h3 className="font-semibold text-slate-900 mb-2 text-sm">All Accounts</h3>
            <div className="text-sm space-y-1">
              {accounts.slice(0, 10).map((a, idx) => (
                <div key={idx} className="flex justify-between py-1 border-b border-slate-100">
                  <span className="text-slate-700 truncate">{a.creditor_name} · {a.account_number_masked}</span>
                  <span className="text-xs text-slate-500 whitespace-nowrap ml-2">{a.status}</span>
                </div>
              ))}
              {accounts.length > 10 && (
                <div className="text-xs text-slate-500 pt-1">…and {accounts.length - 10} more</div>
              )}
            </div>
          </div>
        )}
      </div>

      <DialogFooter className="mt-5 flex-col sm:flex-row gap-2">
        <Button variant="outline" onClick={onClose} disabled={generating}>Close</Button>
        <Button
          onClick={generateLetter}
          disabled={selectedCount === 0 || generating}
          className="btn-primary flex-1"
          data-testid="generate-letter-btn"
        >
          {generating ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating letter...</>
          ) : (
            <><Mail className="h-4 w-4 mr-2" /> Generate Dispute Letter ({selectedCount} {selectedCount === 1 ? 'item' : 'items'})</>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  const tones = {
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <div className={`p-3 rounded-lg border ${tones[tone] || tones.slate}`}>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-0.5" style={{ fontFamily: 'Manrope' }}>{value}</div>
    </div>
  );
}
